import {
  constructRow,
  tableMemo,
  type Row,
  type RowData,
  type RowModel,
  type Table,
  type Table_Internal,
  type TableFeatures,
} from '@tanstack/react-table';

export function createCoreRowModel<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(): (table: Table<TFeatures, TData>) => () => RowModel<TFeatures, TData> {
  return (_table) => {
    const table = _table as Table_Internal<TFeatures, TData>;
    return tableMemo({
      feature: 'coreRowModelsFeature',
      table,
      fnName: 'table.getCoreRowModel',
      memoDeps: () => [table.options.data, table.options.columns],
      fn: () => _createCoreRowModel(table, table.options.data),
    });
  };
}

function _createCoreRowModel<
  TFeatures extends TableFeatures,
  TData extends RowData,
>(
  table: Table_Internal<TFeatures, TData>,
  data: ReadonlyArray<TData>,
): {
  rows: Array<Row<TFeatures, TData>>;
  flatRows: Array<Row<TFeatures, TData>>;
  rowsById: Record<string, Row<TFeatures, TData>>;
} {
  const rowModel: RowModel<TFeatures, TData> = {
    rows: [],
    flatRows: [],
    rowsById: {},
  };

  const accessRows = (
    originalRows: ReadonlyArray<TData>,
    depth = 0,
    parentRow?: Row<TFeatures, TData>,
  ): Array<Row<TFeatures, TData>> => {
    const rows = [] as Array<Row<TFeatures, TData>>;

    for (let i = 0; i < originalRows.length; i++) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const originalRow = originalRows[i]!;
      // Make the row
      const row = constructRow(
        table,
        table.getRowId(originalRow, i, parentRow),
        originalRow,
        i,
        depth,
        undefined,
        parentRow?.id,
      );

      // Keep track of every row in a flat array
      rowModel.flatRows.push(row);
      // Also keep track of every row by its ID
      rowModel.rowsById[row.id] = row;
      // Push table row into parent
      rows.push(row);

      // Get the original subrows
      if (table.options.getSubRows) {
        row.originalSubRows = table.options.getSubRows(originalRow, i);

        // Then recursively access them
        if (row.originalSubRows?.length) {
          row.subRows = accessRows(row.originalSubRows, depth + 1, row);
        }
      }
    }

    return rows;
  };

  rowModel.rows = accessRows(data);

  return rowModel;
}
